// @flow
import * as React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { getImageUrl, getItemUrl } from '../../utils/urls';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback';
import isEqual from 'react-fast-compare';
import type { ItemCard } from '../../types/ItemCard';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styles from './PriceResults.module.scss';

type Props = $ReadOnly<{|
    count: number,
    creator?: string,
    isLoggedIn: boolean,
    items?: ItemCard[],
    seeAllUrl: string,
|}>;

export const buildImageElements = (cards: ItemCard[], isLoggedIn: boolean): React.Node => {
    return cards.map((card: ItemCard, index: number): React.Node => {
        const { biddingInfo, catalog, item } = card;
        const { catalogId, itemId, photos, sellerId } = item;
        const itemUrl = getItemUrl(item.itemId, item.title);
        const { salePrice, isSold } = biddingInfo;
        const { currency } = catalog;

        const itemImageUrl = getImageUrl({
            catalogId: catalogId,
            imageNumber: photos?.[0] || 1,
            itemId: itemId,
            sellerId: sellerId,
            width: 212,
        });

        let priceLabel;
        if (typeof isSold === 'undefined') {
            priceLabel = '';
        } else if (isSold && isLoggedIn) {
            priceLabel = (
                <FormattedNumber
                    value={salePrice}
                    style="currency"
                    currency={currency}
                    maximumFractionDigits={0}
                    minimumFractionDigits={0}
                />
            );
        } else if (isSold && !isLoggedIn) {
            priceLabel = <FormattedMessage id="searchResults.sold" />;
        } else {
            priceLabel = <FormattedMessage id="searchResults.passed" />;
        }

        return (
            <div key={`${itemId}-${index}`} className={styles.item}>
                <Link key={`image-index-${index}`} route to={itemUrl} className={styles.imageWrapper}>
                    <ImageWithFallback className={styles.image} src={itemImageUrl} />
                </Link>
                <div className={styles.price}>{priceLabel}</div>
            </div>
        );
    });
};

const PriceResults = ({ count: preFormattedCount, creator = '', isLoggedIn, items = [], seeAllUrl }: Props) => {
    return (
        <div className={styles.priceResults}>
            <div className={styles.top}>
                <Link route to={seeAllUrl} className={styles.link}>
                    {creator ? (
                        <FormattedMessage id="see_auction_price_results_for" values={{ token: creator }} />
                    ) : (
                        <FormattedMessage
                            id="searchResults.seeAllPriceResults"
                            values={{
                                count: (
                                    <FormattedNumber
                                        value={preFormattedCount}
                                        maximumFractionDigits={0}
                                        minimumFractionDigits={0}
                                    />
                                ),
                            }}
                        />
                    )}
                </Link>
            </div>
            <div className={styles.images}>{buildImageElements(items, isLoggedIn)}</div>
        </div>
    );
};

export default React.memo<Props>(PriceResults, isEqual);
